function getTripDetails ({booking, departure, confirmation, airports}) {
  const priceBreakdown = booking.booking && booking.booking.priceBreakdown
  const {outboundFlight, returnFlight} = getFlightDetails(priceBreakdown, airports)
  return {
    currency: departure.prices.currency,
    outboundFlight,
    preAccommodation: priceBreakdown.extraStays.before,
    tripStart: {
      date: departure.startDate,
      city: departure.startPlace.city,
      country: departure.startPlace.country,
    },
    tripEnd: {
      date: departure.endDate,
      city: departure.endPlace.city,
      country: departure.endPlace.country,
    },
    postAccommodation: priceBreakdown.extraStays.after,
    returnFlight,
    prices: [
      ...priceBreakdown.prices,
      getCardFeePrice(confirmation),
    ].filter(Boolean),
    total: priceBreakdown.totalPrice,
    payment: getPaymentDetails(confirmation, priceBreakdown.totalPrice),
  }
}

function getFlightDetails (priceBreakdown, airports) {
  if (priceBreakdown.promisedFlights && priceBreakdown.promisedFlights.legs.length > 0) {
    return getDetailsFromFlights(priceBreakdown.promisedFlights, airports)
  }

  if (priceBreakdown.flights && priceBreakdown.flights.legs && priceBreakdown.flights.legs.length > 0){
    return getDetailsFromFlights(priceBreakdown.flights, airports)
  }
  return {}
}

function getDetailsFromFlights (flights, airports) {
  const {legs} = flights
  const outboundFlight = getFlightDetailsFromLeg(legs[0])
  const returnFlight = getFlightDetailsFromLeg(legs[legs.length - 1])
  setAirportNames(airports, [outboundFlight, returnFlight])
  return {outboundFlight, returnFlight}
}

function setAirportNames (airports, flights) {
  airports && flights.forEach(flight => {
    const departureAirport = airports.find(airport => airport.code === flight.depart)
    if (departureAirport) {
      flight.departureAirport = `${departureAirport.name}, ${departureAirport.city}`
    }
    const arrivalAirport = airports.find(airport => airport.code === flight.arrive)
    if (arrivalAirport) {
      flight.arrivalAirport = `${arrivalAirport.name}, ${arrivalAirport.city}`
    }
  })
}

function getFlightDetailsFromLeg (leg) {
  return {
    arrive: leg.arrive || leg.lastFlight.arrive,
    cabin: leg.cabin || leg.firstFlight.cabin,
    departDate: leg.date || leg.firstFlight.departDate,
    departTime: leg.firstFlight && leg.firstFlight.departDate,
    depart: leg.depart || leg.firstFlight.depart,
    isMixedCabin: leg.isMixedCabin,
  }
}

function getCardFeePrice (confirmation) {
  const {fee} = extractAmountAndFee(confirmation)
  return fee && {
    name: 'Fee',
    price: fee,
  }
}

function extractAmountAndFee (confirmation) {
  if (!confirmation || !(typeof confirmation === 'object')) return {}
  const {creditCardPayment, paypalPayment, paymentGateway} = confirmation
  const {amount, fee = 0} = (creditCardPayment || paypalPayment || paymentGateway || {})
  return {amount, fee}
}

function getPaymentDetails (confirmation, total) {
  if (!confirmation || !(typeof confirmation === 'object')) return
  const {amount, fee} = extractAmountAndFee(confirmation)
  const outstanding = total - (amount - fee)
  const confirmationType = getConfirmationType(confirmation)

  switch (confirmationType) {
  case 'CreditCardConfirmation':
    return {
      type: 'card',
      amount,
      fee,
      outstanding,
    }
  case 'PayPalConfirmation':
    return {
      type: 'paypal',
      amount,
      fee,
      outstanding,
    }
  case 'PaymentGatewayConfirmation' :
    return {
      type: 'paymentGateway',
      amount,
      fee,
      outstanding,
    }
  case 'CreditAgreementConfirmation':
  case 'DirectDepositConfirmation':
  case 'ChequeConfirmation':
  case 'MCOWRAConfirmation':
  case 'PaymentGateConfirmation':
  case 'ElectronicFundsTransferConfirmation':
  case 'EnettConfirmation':
  case 'SmartMoneyConfirmation':
  case 'ZipMoneyConfirmation':
    return {
      type: confirmationType,
      amount: total,
      outstanding: 0,
    }
  case 'TourOnRequestConfirmation':
    return {
      type: 'On request',
      amount: 0,
      outstanding: 0,
    }
  default:
    return {
      type: 'On hold',
      amount: 0,
      outstanding: total,
    }
  }
}

function getConfirmationType (confirmation) {
  if (confirmation.onHold) {
    return 'OnHoldConfirmation'
  } else if (confirmation.creditAgreementPayment) {
    return 'CreditAgreementConfirmation'
  } else if (confirmation.creditCardPayment) {
    return 'CreditCardConfirmation'
  } else if (confirmation.directDeposit) {
    return 'DirectDepositConfirmation'
  } else if (confirmation.tourOnRequest) {
    return 'TourOnRequestConfirmation'
  } else if (confirmation.cheque) {
    return 'ChequeConfirmation'
  } else if (confirmation.mcowra) {
    return 'MCOWRAConfirmation'
  } else if (confirmation.paymentGate) {
    return 'PaymentGateConfirmation'
  } else if (confirmation.electronicFundsTransfer) {
    return 'ElectronicFundsTransferConfirmation'
  } else if (confirmation.eNett) {
    return 'EnettConfirmation'
  } else if (confirmation.smartMoney) {
    return 'SmartMoneyConfirmation'
  } else if (confirmation.paypalPayment) {
    return 'PayPalConfirmation'
  } else if (confirmation.fake) {
    return 'FakeConfirmation'
  } else if (confirmation.zipMoney) {
    return 'ZipMoneyConfirmation'
  } else if (confirmation.paymentGateway) {
    return 'PaymentGatewayConfirmation'
  }
}

export {
  getTripDetails,
  getConfirmationType,
}
